import React from "react";
import {
  useFormikContext,
  FormikContextType,
  FieldAttributes,
  useField,
  ErrorMessage,
} from "formik";
import toPath from "lodash/toPath";
import classNames from "classnames";
import { FormField } from "@avamae/formbuilder";

/**
 * Given an object and a path, traverse the object to find the value,
 * and return it.
 */
export function deepGet(obj: any, path: string | string[]) {
  const segments = toPath(path);
  let i = 0;
  let val = obj;
  for (; i < segments.length; i++) {
    if (val === undefined) {
      return val;
    }
    val = val[segments[i]];
  }

  return val;
}

function Input<T>(props: FieldAttributes<T>) {
  const [field, meta] = useField(props);
  const showError = meta.touched && meta.error;
  const { className: existingClasses, ...rest } = props;
  const className = classNames(existingClasses, { ErrorInput: showError });

  return <input {...(field as any)} {...rest} className={className} />;
}

const ErrorM: React.FC<{ name: string }> = props => (
  <ErrorMessage name={props.name}>
    {msg => <div className="ErrorLabel">{msg}</div>}
  </ErrorMessage>
);

export interface Booleanv2Props {
  fieldName: string;
  label: string;
  readOnly?: boolean;
  labelOverride?: BooleanLabelOverride;
  allowNullValue?: boolean;
  editReadOnly?: boolean;
}

const Booleanv2 = function<T>({
  fieldName,
  label,
  readOnly,
  labelOverride,
  allowNullValue,
  editReadOnly,
}: Booleanv2Props) {
  const formikContext = useFormikContext<FormikContextType<T>>();

  const fieldLabelOverride =
    labelOverride && labelOverride.hasOwnProperty(fieldName)
      ? labelOverride[fieldName]
      : null;

  if (readOnly) {
    return (
      <div className="FormBox FormSideBySide">
        <div className="FormLabel">
          <label>{label}</label>
        </div>
        <div className="FormField">
          <Input
            name={fieldName}
            className={"EditBox ViewOnly"}
            readOnly={readOnly}
            value={deepGet(formikContext.values, fieldName) ? "Yes" : "No"}
          />
        </div>
      </div>
    );
  } else {
    return (
      <div
        className={`FormBox FormSideBySide Boolean ${
          editReadOnly ? "BooleanEditReadOnly EditReadOnly" : ""
        }`}
      >
        {/* <div className="FormLabel NoPaddingTop"> */}
        <div className="FormLabel">
          <label>{label}</label>
        </div>

        <div className="FormField">
          <div className="RadioButtons">
            <div
              className={`Radio ${
                deepGet(formikContext.values, fieldName) === (true || "Yes")
                  ? "Checked"
                  : ""
              }`}
              role="checkbox"
              aria-checked={deepGet(formikContext.values, fieldName)}
              onClick={() => {
                if (!editReadOnly) {
                  formikContext.setFieldValue(fieldName as any, true);
                }
              }}
            >
              <input
                id={fieldName + "-yes"}
                type="radio"
                name={fieldName}
                value={"true"}
                checked={deepGet(formikContext.values, fieldName)}
                onChange={() => {}} // Prevent console error about providing a checked attribute but no onChange prop. The onChange is the onClick on the div that wraps this.
              />
              <label className="CustomInput" htmlFor={fieldName + "-yes"}>
                {fieldLabelOverride && fieldLabelOverride.true
                  ? fieldLabelOverride.true
                  : "Yes"}
              </label>
            </div>

            <div
              className={`Radio ${
                // deepGet(formikContext.values, fieldName) === (false || "No")
                !deepGet(formikContext.values, fieldName) ? "Checked" : ""
              }`}
              role="checkbox"
              aria-checked={!deepGet(formikContext.values, fieldName)}
              onClick={() => {
                if (!editReadOnly) {
                  formikContext.setFieldValue(fieldName as any, false);
                }
              }}
            >
              <input
                id={fieldName + "-no"}
                type="radio"
                name={fieldName}
                value={"false"}
                checked={!deepGet(formikContext.values, fieldName)}
                onChange={() => {}} // Prevent console error about providing a checked attribute but no onChange prop. The onChange is the onClick on the div that wraps this.
              />
              <label className="CustomInput" htmlFor={fieldName + "-no"}>
                {fieldLabelOverride && fieldLabelOverride.false
                  ? fieldLabelOverride.false
                  : "No"}
              </label>
            </div>

            {allowNullValue && (
              <div
                className={`Radio ${
                  // deepGet(formikContext.values, fieldName) === (false || "No")
                  deepGet(formikContext.values, fieldName) === null
                    ? "Checked"
                    : ""
                }`}
                role="checkbox"
                aria-checked={deepGet(formikContext.values, fieldName) === null}
                onClick={() => {
                  if (!editReadOnly) {
                    formikContext.setFieldValue(fieldName as any, false);
                  }
                }}
              >
                <input
                  id={fieldName + "-null"}
                  type="radio"
                  name={fieldName}
                  value={"null"}
                  checked={deepGet(formikContext.values, fieldName) === null}
                  onChange={() => {}} // Prevent console error about providing a checked attribute but no onChange prop. The onChange is the onClick on the div that wraps this.
                />
                <label className="CustomInput" htmlFor={fieldName + "-null"}>
                  {fieldLabelOverride && fieldLabelOverride.false
                    ? fieldLabelOverride.false
                    : "No"}
                </label>
              </div>
            )}
          </div>
        </div>
        <ErrorM name={fieldName.toString()} />
      </div>
    );
  }
};

export { Booleanv2 };

type BooleanLabelOverride = {
  [fieldName: string]: {
    true: string;
    false: string;
    null?: string;
  };
};

export const booleanV2Renderer = (
  readOnly: boolean,
  labelOverride?: BooleanLabelOverride,
  allowNullValue?: boolean,
  editReadOnly?: boolean
) =>
  function booleanV2Renderer<T>(_key: React.ReactText, field: FormField<T>) {
    return (
      <Booleanv2
        label={field.label as string}
        readOnly={readOnly}
        fieldName={field.name as string}
        labelOverride={labelOverride}
        allowNullValue={allowNullValue}
        editReadOnly={editReadOnly}
      />
    );
  };
